import { useVenueEntitlements } from "./entitlements";
import { useOnboardingProgress } from "./useOnboardingProgress";

export type AvailabilityStatus = "available" | "needs-plan" | "not-live";

export interface FeatureAvailability {
  status: AvailabilityStatus;
  /** The entitlement key when `status === "needs-plan"` (drives the plan reverse-lookup + copy). */
  feature?: string;
}

export interface FeatureRequirements {
  /** Entitlement (plan) the feature needs. */
  feature?: string;
  /** Whether the venue must be publicly LIVE for the feature to actually function. */
  requiresLive?: boolean;
}

/**
 * Pure resolver — WHY a feature is (un)available, in priority order: plan gate first (a locked plan is
 * the hard stop), then the venue-not-live gate. Config-specific prerequisites (e.g. place-match needs a
 * confirmed address) stay feature-local. Split out from the hook so it's unit-testable.
 */
export function resolveAvailability(
  req: FeatureRequirements,
  ctx: { has: (key: string) => boolean; isLive: boolean },
): FeatureAvailability {
  if (req.feature && !ctx.has(req.feature)) return { status: "needs-plan", feature: req.feature };
  if (req.requiresLive && !ctx.isLive) return { status: "not-live" };
  return { status: "available" };
}

/**
 * Resolves WHY a single feature is (un)available. For lists (nav), read `has()` +
 * `useOnboardingProgress()` directly and compute inline — hooks can't run in a loop.
 */
export function useFeatureAvailability(req: FeatureRequirements): FeatureAvailability {
  const { has } = useVenueEntitlements();
  const { isLive } = useOnboardingProgress();
  return resolveAvailability(req, { has, isLive });
}
